/* ============================================================
   Scroll reveal - one observer for the whole document

   Styles: css/frontend/utilities/reveal.scss. Ported from the jkdevstudio theme
   (pass 398.116) unchanged - the two sites share this arrival, and a second
   implementation is a second set of timings free to drift.

   WHAT IT IS: an element marked `data-reveal` arrives - a short rise, a fade and a
   blur resolving together over most of a second on a long-tailed curve. Nothing
   else. It is a single effect applied consistently rather than a library of
   entrances, because a page where six things arrive six different ways is a page
   that is animating rather than one that is being read.

   ── THE RESTING STATE IS SET BY THIS SCRIPT, NEVER PRINTED IN THE MARKUP
   `data-reveal` is the author's request; `data-reveal-armed` is this file agreeing
   to it, and only the second one has any CSS behind it. So a browser with JS off, a
   script that 404s, a parse error three files up - every one of those ends with the
   page rendering finished instead of blank. This is the single most important line
   in the file and it is the one that is easiest to "simplify" away by moving the
   hidden state into the stylesheet.

   ── ONE OBSERVER, NOT ONE PER ELEMENT
   An IntersectionObserver is cheap to consult and expensive to create; a page with
   forty cards should have one of them, not forty. Elements are added to the shared
   observer and dropped from it the moment they have arrived, so a fully-revealed
   page costs nothing at all.

   ── WHY AN OBSERVER RATHER THAN A SCROLL HANDLER
   Lenis drives this document from its own rAF loop and the browser emits no
   `scroll` event for those frames (see lib/smooth-scroll), so a scroll listener here
   would fire on a scrollbar drag and nothing else. An observer is told by the engine
   and does not care what is moving the page - which also makes it correct inside a
   scrolling container, on a zoomed page, and on a phone doing rubber-band overscroll.

   ── THE STAGGER IS PER BATCH, NOT PER LIST
   Twelve cards that enter together should cascade; twelve cards spread down a long
   page should each arrive on their own. So the delay is counted over the elements in
   ONE observer callback - the ones that genuinely crossed the line in the same
   moment - and it is capped, because a cascade longer than about half a second stops
   reading as one gesture and starts reading as a queue.

   ── AND IT CLEANS UP AFTER ITSELF
   `filter: blur()` and `will-change` each put an element on its own compositor layer.
   That is what makes the arrival smooth and what makes forty of them expensive to
   keep, so both are dropped the moment the transition ends.
   ============================================================ */

/* The attribute an author writes, and the one this file writes back. */
const REQUEST = '[data-reveal], [data-reveal-group] > *';
const ARMED   = 'data-reveal-armed';

/* How far up the viewport an element has to come before it counts as arrived. A
   negative bottom margin moves the trigger line up off the very edge of the screen,
   so nothing starts resolving while it is still technically a pixel below the fold. */
const ROOT_MARGIN = '0px 0px -10% 0px';

/* One step of the cascade, and the most the cascade may ever add up to. */
const STAGGER_MS = 85;
const STAGGER_CAP_MS = 420;

/* Long enough that the blur has somewhere to resolve from; the curve does the rest. */
const DURATION_MS = 900;

/* What the first batch waits before it starts - see the note in onIntersect(). */
const OPENING_MS = 80;
let opened = false;

let observer = null;

/* ── THE WATCHDOG, AND IT IS NOT OPTIONAL ───────────────────────────────────────
   Arming an element hides it, so from that moment the page's content depends on a
   callback arriving. Keeping the hidden state out of the stylesheet protects against
   the script never RUNNING; it does nothing about the script running and the observer
   then never reporting - a hidden tab that Chrome has throttled, an engine quirk, a
   container this was not tested inside. The failure is total and silent: an archive
   of ten cards, all armed, all at opacity 0, for ever. It was measured in exactly
   that state on the live site before this existed.

   AN OBSERVER ALWAYS DELIVERS A FIRST CALLBACK for its targets, intersecting or not.
   So "no callback at all, two seconds after arming the first element" is a reliable
   signal that the mechanism is not working here, and the answer is to show
   everything and get out of the way. Losing an animation is nothing; losing the page
   is the only outcome that matters. */
const WATCHDOG_MS = 2000;
let heard = false;
let watching = false;

function watchdog() {
    if (watching) {
        return;
    }
    watching = true;

    window.setTimeout(() => {
        if (heard) {
            return;
        }
        /* ONE CLASS OFF THE ROOT SHOWS EVERYTHING. The resting state is a stylesheet
           rule scoped to `.jkds-reveal` (utilities/reveal.scss), so removing it is the
           whole recovery - no walk over the elements, and it covers anything inserted
           after this point too. */
        document.documentElement.classList.remove('jkds-reveal');
        if (observer) {
            observer.disconnect();
            observer = null;
        }
    }, WATCHDOG_MS);
}

function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Finish an element: drop the layer-forcing properties and stop watching it.
 *
 * The transition is listened for rather than timed, so a browser that ran it faster
 * or slower than the stylesheet asked (a background tab, a throttled device) still
 * gets its layer back at the right moment.
 */
function settle(el) {
    const done = () => {
        el.removeAttribute(ARMED);
        el.setAttribute('data-reveal-done', '');
    };

    // The arrival is a keyframe animation now (utilities/reveal.scss), so this is the
    // event that says it finished - `transitionend` never fires and every element
    // would sit on a compositor layer for the life of the page.
    el.addEventListener('animationend', done, { once: true });

    /* A BELT FOR THE BRACES. `animationend` does not fire for an element that was
       never painted - a hidden tab, `content-visibility`, a parent that is
       `display:none` when the class lands - and an element left armed keeps a
       compositor layer for the life of the page. */
    window.setTimeout(done, DURATION_MS + STAGGER_CAP_MS + 400);
}

function onIntersect(entries) {
    heard = true;
    let step = 0;

    const OPENING = opened ? 0 : OPENING_MS;
    opened = true;

    for (const entry of entries) {
        if (! entry.isIntersecting) {
            continue;
        }

        const el = entry.target;
        observer.unobserve(el);

        /* ALREADY ON SCREEN AT FIRST PAINT IS NOT A CASCADE. Everything above the
           fold intersects in the very first callback, and staggering that turns the
           top of the page into a queue of things waiting their turn. They arrive
           together; the cascade is for what is scrolled to.

           THE FIRST BATCH WAITS A BEAT, though, and that is not decoration. It is the
           one that fires while the page is still assembling - fonts swapping, images
           decoding, the layout settling - so an arrival that starts immediately is an
           arrival nobody sees, competing with everything else moving at once. A fifth
           of a second is enough for the page to be still when it starts. */
        /* AN ELEMENT MAY ASK TO COME LAST, and `data-reveal-delay` is the whole of
           how - a number of milliseconds ADDED to whatever the stagger worked out.
           The hero's logo strip uses it: the centre stack and the two side columns are
           one arrival, and the row of places that published us is a footnote to it,
           not a fifth item in the queue. Still the one handler and the one keyframe;
           an element that wants to be later says so in the markup rather than getting
           an entrance of its own. */
        const asked = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);

        /* AND AN ELEMENT MAY ALSO DECLARE ITS PLACE IN THE CASCADE, which is a
           different thing from asking to be later. `data-reveal-delay` ADDS to
           whatever position the batch worked out; `data-reveal-step` REPLACES it.

           The difference matters wherever two runs of elements should arrive as
           PAIRS rather than as a queue. The hero's ticker columns are the case: two
           down the left margin and two down the right, and the design is the first of
           each side together, then the second of each. In document order they are
           left, left, right, right - a queue of four, and no amount of added delay
           can pull the third back level with the first, because a delay only ever
           pushes forward.

           Stated as steps 0, 1, 0, 1 the two sides pair on their own, and nothing has
           to know how many elements happen to be in the batch or what the stagger is
           set to. An element with no step keeps counting the batch, exactly as
           before. */
        const step_attr = parseInt(el.getAttribute('data-reveal-step') || '', 10);
        const position  = Number.isFinite(step_attr) ? Math.max(0, step_attr) : step;

        const delay = OPENING
            + Math.min(position * STAGGER_MS, STAGGER_CAP_MS)
            + (Number.isFinite(asked) ? Math.max(0, asked) : 0);
        step += 1;

        el.style.setProperty('--reveal-delay', delay + 'ms');
        // Next frame, so the armed resting state has actually been painted - set in
        // the same frame the element is armed, the transition has nothing to run from.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => el.classList.add('is-revealed'));
        });
        settle(el);
    }
}

/**
 * Arm one element: hide it, then watch for it.
 *
 * Order matters. The attribute is what the stylesheet's resting state hangs on, so it
 * has to be there before the observer can possibly fire - otherwise an element that
 * is already on screen gets `is-revealed` while it is still fully visible, and
 * nothing animates at all.
 */
/**
 * Watch one element. It is ALREADY hidden - the stylesheet did that before the first
 * paint - so this only marks it as spoken for and hands it to the observer.
 */
function arm(el) {
    if (el.hasAttribute(ARMED) || el.hasAttribute('data-reveal-done')) {
        return;
    }
    el.setAttribute(ARMED, '');
    observer.observe(el);
    watchdog();
}

/* A TAB THAT WAS NEVER PAINTED HAS NOT FAILED, IT HAS NOT STARTED. Chrome does not
   run an IntersectionObserver for a background tab, so a page opened in one and left
   there trips the watchdog and is simply shown - correct, and the reason the watchdog
   reveals rather than retries. But if the visitor comes back BEFORE it fires, the
   observer starts reporting and nothing was lost; resetting the clock on that moment
   is what keeps a cmd-clicked tab animating instead of arriving already finished. */
document.addEventListener('visibilitychange', () => {
    if (! document.hidden && ! heard && observer) {
        watching = false;
        watchdog();
    }
});

function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(REQUEST).forEach(arm);

    // A root that is itself a request - a node inserted by a script, not a container.
    if (root && root.matches && root.matches(REQUEST)) {
        arm(root);
    }
}

function init() {
    /* NOTHING IS ARMED AT ALL for a visitor who asked for less motion. Not "armed and
       revealed instantly" - never hidden in the first place, so there is no frame in
       which the page is blank for them either. */
    if (reduced() || ! ('IntersectionObserver' in window)) {
        return;
    }

    observer = new IntersectionObserver(onIntersect, {
        rootMargin: ROOT_MARGIN,
        threshold: 0,
    });

    scan(document);

    /* WHATEVER ARRIVES LATER GETS THE SAME TREATMENT. A load-more append, an Alpine
       template stamped out when a condition turns true, a search result list - none
       of those exist when this runs, and a reveal system that only knows about the
       first paint is one that has to be re-initialised by hand from four other
       files. */
    new MutationObserver((records) => {
        for (const record of records) {
            record.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                    scan(node);
                }
            });
        }
    }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
